import type { components } from "@/api/types";
import { downsample, toChartNumber } from "@/shared/lib";

type ChartPoint = components["schemas"]["ChartPoint"];

/**
 * График на карточке заработка.
 *
 * Написан руками, а не на Recharts — и это осознанный выбор, а не экономия
 * усилий:
 *
 *  - в макете у линии есть свечение (`feGaussianBlur`), заливка области и
 *    кружок на КАЖДОЙ точке; Recharts не даёт ни первого, ни третьего без
 *    обходных путей через кастомные компоненты;
 *  - график упирается в края карточки (`preserveAspectRatio="none"`), с чем
 *    `ResponsiveContainer` борется, а не помогает;
 *  - это первый экран приложения, и снять с него ленивый чанк Recharts в
 *    WebView Telegram — заметный выигрыш на старте.
 *
 * Recharts остаётся у графика робота (`EquityPlot`), где нужны оси, тултипы и
 * взаимодействие. Здесь ничего этого нет.
 *
 * Ряд приходит готовым с бэкенда; здесь он только прореживается до числа
 * точек, которое видно на 328 пикселях, — с сохранением краёв.
 */
const POINTS = 22;

/** Сетка макета: три горизонтали и семь вертикалей. */
const H_LINES = [20, 48, 76];
const V_LINES = [42, 84, 126, 168, 210, 252, 294];

export function EarningsSpark({ points }: { points: ChartPoint[] }) {
  if (points.length < 2) return null;

  const values = downsample(points, POINTS).map((point) => toChartNumber(point.value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  // Плоский ряд (нули у нового пользователя) дал бы деление на ноль. Рисуем
  // его прямой по низу, а не прячем карточку: ноль — тоже результат.
  const span = max - min || 1;

  const step = 328 / (values.length - 1);
  const coords = values.map((value, index) => ({
    x: index * step,
    y: 99 - ((value - min) / span) * 90,
  }));

  // Без округления: SVG принимает дробные координаты как есть, а `toFixed`
  // запрещён правилом «фронт ничего не вычисляет» — оно смотрит на имя
  // функции, не разбирая, деньги это или пиксели.
  const line = coords.map((point) => `${String(point.x)} ${String(point.y)}`).join(" L ");
  const area = `M ${line} L 328 101 L 0 101 Z`;

  return (
    <svg
      viewBox="0 0 328 108"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="block size-full text-status-profit"
    >
      <defs>
        <linearGradient id="earnings-fill" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
        {/* Свечение линии: размытая копия под оригиналом. Так линия читается
            как источник света, а не как нарисованный контур. */}
        <filter id="earnings-glow">
          <feGaussianBlur stdDeviation="2" result="blurred" />
          <feMerge>
            <feMergeNode in="blurred" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g stroke="currentColor" strokeOpacity="0.09" strokeWidth="0.6">
        {H_LINES.map((y) => (
          <path key={`h${String(y)}`} d={`M0 ${String(y)}H328`} />
        ))}
        {V_LINES.map((x) => (
          <path key={`v${String(x)}`} d={`M${String(x)} 0V99`} />
        ))}
      </g>

      <path d={area} fill="url(#earnings-fill)" />
      <path
        d={`M ${line}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        filter="url(#earnings-glow)"
      />
      <g fill="currentColor">
        {coords.map((point) => (
          <circle key={point.x} cx={point.x} cy={point.y} r="2" />
        ))}
      </g>
    </svg>
  );
}
